// ABMSalesPlaybook.js — Coursera-branded Sales PDF (Segment #3)
// Purpose: ABM-centric, action-oriented playbook for AEs (print-ready)
// Drop-in single file React component. No external CSS. Uses window.print() for PDF.

import React, { useEffect } from "react";

/* ==================== Assets ==================== */
const LOGO_SVG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.svg";
const LOGO_PNG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.png";
const C_RGB =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/conduit/RGB_Conduit.svg";
const THUMB_SALES =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abmsales_101.png";
const THUMB_SDR =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abmsdr_101.png"; // if you also re-use

/* ==================== Quick hook ==================== */
function useAutoTitle(title) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => (document.title = prev);
  }, [title]);
}

/* ==================== Local CSS ==================== */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700;800&display=swap');
:root{ --blue:#2A73CC; --navy:#0E2B57; --ink:#0B1A3C; --ink70:#0B1A3CB3; --line:#E6EDF2; --card:#fff; --bg:#F4F6FA; --shadow:0 8px 28px rgba(11,26,60,.12) }
*{box-sizing:border-box}
body{margin:0; font-family:'Source Sans Pro',system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif; background:var(--bg); color:var(--ink)}
.pdf-top{position:sticky;top:0;z-index:40;background:#fff;border-bottom:1px solid var(--line)}
.pdf-top__in{max-width:1120px;margin:0 auto;display:flex;align-items:center;gap:12px;justify-content:space-between;padding:12px 16px}
.brand{display:flex;align-items:center;gap:8px;color:var(--ink);text-decoration:none;font-weight:800}
.brand small{color:var(--ink70);font-weight:700}
.btn{display:inline-flex;align-items:center;gap:8px;padding:10px 14px;border-radius:10px;border:1px solid var(--line);background:#fff;font-weight:700;color:var(--navy);cursor:pointer}
.btn--pri{background:var(--blue);border-color:transparent;color:#fff}
.btn:hover{filter:brightness(.98)}

.sheet{max-width:1120px;margin:16px auto;}
.page{position:relative;background:#fff;border:1px solid var(--line);border-radius:16px;box-shadow:var(--shadow);padding:24px;margin:16px}
.page__foot{position:absolute;bottom:16px;right:20px;color:#64748B;font-size:12px}
.hero{position:relative;border-radius:16px;overflow:hidden;color:#fff;padding:28px;background:
  radial-gradient(1200px 420px at 120% -30%, rgba(255,255,255,.16), transparent 60%),
  linear-gradient(180deg, var(--blue) 0%, #1f62b2 100%)}
.hero__wm{position:absolute;right:-120px;top:-80px;width:420px;opacity:.18}
.hero h1{margin:0 0 6px;font-weight:800;font-size:40px;line-height:1.1}
.hero p{margin:0 0 10px;opacity:.95;font-size:18px}
.lede{max-width:760px;background:rgba(255,255,255,.10);border:1px solid rgba(255,255,255,.26);border-radius:12px;padding:12px}

.kicker{font-size:14px;color:var(--ink70);text-transform:uppercase;letter-spacing:.3px;font-weight:800;margin:0 0 6px}
.h2{margin:0 0 6px;font-size:26px;line-height:1.2;font-weight:800;color:var(--navy)}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
@media (max-width:960px){ .grid{grid-template-columns:1fr} }
.panel{background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px}
.panel--tint{background:#F8FAFE;border-color:#D9E7FF}
.panel h3{margin:0 0 8px;font-size:18px}
.ul{margin:8px 0 0;padding-left:18px}
.ul li{margin:6px 0}
.note{margin-top:10px;padding:10px 12px;border:1px dashed var(--line);border-radius:12px;background:#FBFCFF;color:#334155}
.badge{display:inline-block;background:#EAF2FF;border:1px solid #D6E6FF;color:#0E2B57;font-weight:800;border-radius:999px;padding:4px 10px;font-size:12px}
.flow{margin:0;padding-left:18px}
.flow li{margin:8px 0}
.cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
@media (max-width:1100px){ .cards{grid-template-columns:1fr} }
.card{background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px}
.card h4{margin:0 0 6px;font-size:16px}

@media print{
  .pdf-top{display:none}
  .page{margin:0;border-radius:0;box-shadow:none;border:none;page-break-after:always;padding:20mm}
  .page__foot{right:20mm;bottom:12mm}
}
/* Cover hero layout (mirrors Marketing) */
.intro__hero{
  position:relative; overflow:hidden; color:#fff; border-radius:16px;
  height: clamp(360px, 42vw, 420px); padding:28px;
  background:
    radial-gradient(1200px 480px at 115% -20%, rgba(255,255,255,.18), rgba(255,255,255,0) 60%),
    linear-gradient(180deg, var(--c-blue) 0%, #1f62b2 100%);
}
.intro__title{margin:0 0 6px; font-size:40px; line-height:48px; font-weight:800}
.intro__subtitle{margin:0 0 12px; font-size:18px; line-height:26px; color:rgba(255,255,255,.95)}
.lede{max-width:780px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.18);
  color:#EAF2FF; padding:10px 12px; border-radius:12px}

/* Right-side thumbnail (like Marketing) */
.intro__thumbWrap{
  position:absolute; right:20px; top:20px; bottom:20px;
  display:flex; align-items:center; justify-content:center;
  width:min(46%, 520px); pointer-events:none;
}
.intro__thumb{
  max-width:100%; max-height:100%; object-fit:contain; border-radius:14px;
  box-shadow:0 18px 50px rgba(0,0,0,.25);
  border:1px solid rgba(255,255,255,.25);
  background:#fff;
}
@media (max-width:900px){
  .intro__thumbWrap{ display:none; } /* hide on small screens like the Marketing cover */
}
/* ===== Sales P1 two-column hero ===== */
.hero2{
  position:relative; color:#fff; border-radius:16px; overflow:hidden;
  padding:28px; min-height:420px;
  background:
    radial-gradient(1200px 480px at 115% -20%, rgba(255,255,255,.18), rgba(255,255,255,0) 60%),
    linear-gradient(180deg, var(--c-blue) 0%, #1f62b2 100%);
  display:grid; grid-template-columns: 1.25fr 1fr; gap:28px; align-items:center;
}
.hero2__left{z-index:1}
.hero2__title{margin:0 0 8px; font-weight:800; font-size:40px; line-height:48px}
.hero2__sub{margin:0 0 14px; font-size:18px; line-height:26px; opacity:.98}
.hero2__lede{
  margin:0; padding:12px 14px; border-radius:12px;
  background:rgba(255,255,255,.10); border:1px solid rgba(255,255,255,.22);
  font-size:16px; line-height:24px; color:#EAF2FF;
}
.hero2__right{
  display:flex; align-items:center; justify-content:center;
}
.device{
  width:100%; max-width:540px; border-radius:20px; overflow:hidden; background:#fff;
  border:1px solid #E6EDF2; box-shadow:0 20px 50px rgba(0,0,0,.18);
}
.device img{display:block; width:100%; height:auto}
@media (max-width:980px){
  .hero2{grid-template-columns:1fr; padding:22px}
  .hero2__right{order:2}
}
/* two-column hero */
.hero2{
  display:grid;
  grid-template-columns: 58% 42%;
  gap: 24px;
  align-items:center;
  background: radial-gradient(1200px 420px at 120% -30%, rgba(255,255,255,.16), transparent 60%),
              linear-gradient(180deg,#2A73CC 0%, #235fb0 100%);
  color:#fff;
  border-radius:24px;
  padding:32px;
  box-shadow: 0 24px 60px rgba(23,42,79,.25);
  position:relative;
}

.hero2__left { position: relative; z-index: 2; }
.intro__title{ font-size:48px; line-height:1.06; margin:0 0 8px; }
.intro__subtitle{ margin:0 0 16px; opacity:.95; font-weight:600; }
.lede.lede--sales{
  color:#EAF2FF;       /* <- readable */
  opacity:1;           /* <- ensure not faded */
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.22);
  border-radius:14px;
  padding:16px 18px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.08);
}

/* Right card with the thumbnail */
.hero2__right{ display:flex; justify-content:center; }
.device{
  background:#fff;
  border-radius:20px;
  padding:14px;
  border:1px solid #dfe8f5;
  box-shadow: 0 20px 50px rgba(7,22,58,.22);
  width:min(520px, 100%);
}
.device img{
  width:100%; height:auto; display:block; border-radius:12px;
}

`;

/* ==================== Small bits ==================== */
const Section = ({ title, kicker, children, tint = false }) => (
  <section className={"panel" + (tint ? " panel--tint" : "")}>
    {kicker && <p className="kicker">{kicker}</p>}
    {title && <h2 className="h2">{title}</h2>}
    {children}
  </section>
);

/* ==================== Pages ==================== */
function P1_Hero() {
  return (
    <div className="hero2">
      {/* Left: title + lede */}
      <div className="hero2__left">
        <h1 className="intro__title">ABM Account Plans that Win</h1>
        <p className="intro__subtitle">
          A simple example and playbook for Coursera AEs
        </p>

        <div className="lede lede--sales">
          If your account is on our ABM 1:1 or 1:Few list, you already have the
          start of an account plan. This guide shows what “good” looks like: a
          clear view of the buying committee, a few focused plays we run
          together with SDRs and Marketing, and simple rules of engagement that
          keep everyone in sync. The goal is not theory—it’s helping you turn
          our shared ABM work into real pipeline and wins.
        </div>
      </div>

      {/* Right: thumbnail card */}
      <div className="hero2__right">
        <div className="device" aria-hidden="true">
          <img src={THUMB_SALES} alt="" />
        </div>
      </div>
    </div>
  );
}

function P2_GoodLooks() {
  return (
    <div className="grid">
      <Section
        title="What ‘Good’ Looks Like"
        kicker="ABM account plan checklist"
        tint
      >
        <ul className="ul">
          <li>
            <b>One page, not a novel</b> – goal, buying committee, 3–5 big
            moves.
          </li>
          <li>
            <b>Built from our shared TAL</b> – the accounts we selected
            together, not a private list.
          </li>
          <li>
            <b>Clear on the win</b> – what “good” looks like this year and this
            quarter.
          </li>
          <li>
            <b>Grounded in signals</b> – why now, what they’ve engaged with,
            where they are in the journey.
          </li>
          <li>
            <b>Easy to run as a team</b> – AEs, SDRs, and Marketing can all see
            their part at a glance.
          </li>
        </ul>
      </Section>
      <Section title="How It Connects to ABM" kicker="From TAL → plan → plays">
        <ul className="ul">
          <li>
            <b>ABM 1:1 accounts</b> get a named plan: specific objectives,
            contacts, and plays.
          </li>
          <li>
            <b>ABM 1:Few clusters</b> (Financial Services, SaaS/Tech) share a
            core story and motions, with light tailoring by account.
          </li>
          <li>
            <b>The TAL is the source</b>, but the plan is the shortlist of moves
            we’ll actually run together this quarter.
          </li>
        </ul>
      </Section>
    </div>
  );
}

function P3_OneTeam() {
  return (
    <Section title="Working as One Team" kicker="AE • SDR • Marketing" tint>
      <div className="cards">
        <div className="card">
          <h4>AE – owns the strategy</h4>
          <ul className="ul">
            <li>Set the outcome: land, expand, or protect.</li>
            <li>Prioritize which contacts matter most and why.</li>
            <li>Decide which 2–3 ABM plays to run first.</li>
          </ul>
        </div>
        <div className="card">
          <h4>SDR – drives the motion</h4>
          <ul className="ul">
            <li>Turn the plan into sequences and outreach.</li>
            <li>Multi-thread into the buying group using the agreed roles.</li>
            <li>Capture new signals and contacts back into the plan.</li>
          </ul>
        </div>
        <div className="card">
          <h4>Marketing – fuels the story</h4>
          <ul className="ul">
            <li>Bring the right industry and role-based assets.</li>
            <li>Run 1:1 or 1:Few programs that match the plan.</li>
            <li>Surface engagement signals by account and persona.</li>
          </ul>
        </div>
      </div>
    </Section>
  );
}

function P4_BuyingCommittee() {
  return (
    <Section
      title="Buying Committee Map"
      kicker="Who we map for each ABM account"
    >
      <ul className="ul">
        <li>
          <b>L&D / Talent Leader</b> – owns learning strategy; primary value
          owner.
        </li>
        <li>
          <b>Tech / Data Leader</b> – owns platforms, integrations, and
          security.
        </li>
        <li>
          <b>HR / People Leader</b> – cares about experience, retention, DEI.
        </li>
        <li>
          <b>Business Sponsor</b> – owns the business outcome the skills program
          must hit.
        </li>
      </ul>
      <div className="note">
        If we can’t name the person in a box, that’s the first gap in the plan.
      </div>
    </Section>
  );
}

function P5_Rules() {
  return (
    <div className="grid">
      <Section title="Rules of Engagement — Do" kicker="Operate visibly" tint>
        <ul className="ul">
          <li>
            Lead with the business problem each role cares about, not a generic
            Coursera pitch.
          </li>
          <li>
            Log every meaningful touch to the same account plan / CRM notes so
            the story stays intact.
          </li>
          <li>
            Use ABM assets and programs as shared tools—“we’re running this play
            together,” not “marketing’s thing.”
          </li>
        </ul>
      </Section>
      <Section title="Rules of Engagement — Don’t" kicker="Avoid these" tint>
        <ul className="ul">
          <li>
            Spin up side campaigns on an ABM account without looping in SDR and
            Marketing.
          </li>
          <li>
            Blind‑multi‑thread the whole org—start with the mapped buying group.
          </li>
          <li>
            Move stages forward without a clear, agreed next step and owner.
          </li>
        </ul>
      </Section>
    </div>
  );
}

function P6_PeerExample() {
  return (
    <Section
      title="Peer Example – How to Use This Guide"
      kicker="A worked example, not a template factory"
    >
      <ul className="ul">
        <li>Start with the example account plan in this guide.</li>
        <li>
          Notice how the goal, buying committee, signals, and plays all fit on{" "}
          <b>one page</b>.
        </li>
        <li>
          Use it as a mirror for your own ABM 1:1 and 1:Few accounts:
          <ul className="ul">
            <li>
              <i>What’s just as clear?</i>
            </li>
            <li>
              <i>Where do you have gaps—roles, signals, next moves?</i>
            </li>
          </ul>
        </li>
      </ul>
      <div className="note">
        Bring your version to your AE–SDR–Marketing check‑in; refine it
        together.
      </div>
    </Section>
  );
}

function P7_Takeaway() {
  return (
    <Section title="Simple Takeaway for Sellers" kicker="The bar" tint>
      <p>
        If you’re working an ABM account, you shouldn’t be guessing. You should
        be able to point to one page and say:
      </p>
      <p className="badge">
        “Here’s who matters, what we know, and the next few plays we’re running
        together.”
      </p>
      <p>That’s the bar this Sales deliverable is setting.</p>
    </Section>
  );
}

/* ==================== Main ==================== */
export default function ABMSalesPlaybook({ compact = false }) {
  useAutoTitle("ABM Account Plans that Win – Sales Playbook");

  const pages = [
    <P1_Hero key="p1" />,
    <P2_GoodLooks key="p2" />,
    <P3_OneTeam key="p3" />,
    <P4_BuyingCommittee key="p4" />,
    <P5_Rules key="p5" />,
    <P6_PeerExample key="p6" />,
    <P7_Takeaway key="p7" />,
  ];

  return (
    <div className="pdf-wrap">
      <style>{CSS}</style>

      {/* Local header */}
      {!compact && (
        <div className="pdf-top">
          <div className="pdf-top__in">
            <a className="brand" href="#abm-sales">
              <picture>
                <source srcSet={LOGO_SVG} type="image/svg+xml" />
                <img src={LOGO_PNG} alt="Coursera" height={18} />
              </picture>
              <span>for Business</span>
            </a>
            <div style={{ display: "flex", gap: 8 }}>
              <a className="btn" href="#abm-sales">
                Back to hub
              </a>
              <button className="btn btn--pri" onClick={() => window.print()}>
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="sheet">
        {pages.map((node, i) => (
          <section className="page" key={i}>
            {node}
            <footer className="page__foot" aria-hidden>
              {i + 1}
            </footer>
          </section>
        ))}
      </main>
    </div>
  );
}
